import React from 'react';
import Markdown from 'react-markdown';

/**
 * Public-side renderer for the structured block schema produced by `BlockEditor`.
 * Each block has a `type` and type-specific fields. Unknown types fall back to
 * a markdown text block so legacy / experimental content still renders.
 */

const CALLOUT_STYLES = {
  info: { bg: '#EFF6FF', border: '#3B82F6', icon: '💡' },
  warning: { bg: '#FFFBEB', border: '#F59E0B', icon: '⚠️' },
  success: { bg: '#F0FDF4', border: '#22C55E', icon: '✅' },
  error: { bg: '#FFF1F2', border: '#F43F5E', icon: '🚫' },
};

function csvToRows(csv) {
  if (!csv) return [];
  return csv
    .trim()
    .split(/\r?\n/)
    .map((line) => line.split(',').map((c) => c.trim()))
    .filter((row) => row.length >= 2);
}

function MiniBarChart({ rows }) {
  if (!rows.length) return null;
  const [, ...data] = rows; // first row is header
  const max = Math.max(1, ...data.map((r) => Number(r[1]) || 0));
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {data.map((row, i) => {
        const value = Number(row[1]) || 0;
        const pct = Math.max(2, Math.round((value / max) * 100));
        return (
          <div key={i} className="flex items-center gap-3 text-xs">
            <span className="w-24 truncate text-[#5B616E]">{row[0]}</span>
            <div className="flex-1 h-3 bg-[#F5F0E8] rounded">
              <div className="h-3 rounded bg-[#B89B5E]" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-12 text-right font-medium">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

function DataTable({ rows }) {
  if (!rows.length) return null;
  const [header, ...body] = rows;
  return (
    <div className="overflow-x-auto rounded-lg border border-[#E7E0D6]">
      <table className="w-full text-sm">
        <thead className="bg-[#FBF7EF]">
          <tr>
            {header.map((h, i) => (
              <th key={i} className="px-3 py-2 text-left font-medium text-[#5B616E]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, i) => (
            <tr key={i} className="border-t border-[#E7E0D6]">
              {row.map((c, j) => (
                <td key={j} className="px-3 py-2 align-top">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isSafeIframeMarkup(html) {
  if (!html) return false;
  if (!html.includes('<iframe')) return false;
  const lower = html.toLowerCase();
  if (lower.includes('<script') || lower.includes('javascript:') || lower.includes(' onerror=') || lower.includes(' onload=')) {
    return false;
  }
  return true;
}

function renderBlock(block, key) {
  switch (block.type) {
    case 'text':
      return (
        <div key={key} className="prose-news leading-7">
          <Markdown>{block.content || ''}</Markdown>
        </div>
      );
    case 'heading': {
      const level = block.level || 'h2';
      const className =
        level === 'h3'
          ? 'news-masthead text-xl font-bold mt-6 mb-3'
          : level === 'h4'
          ? 'news-masthead text-lg font-bold mt-5 mb-2'
          : 'news-masthead text-2xl font-bold mt-8 mb-4';
      const Tag = level;
      return <Tag key={key} className={className}>{block.content}</Tag>;
    }
    case 'image':
      return (
        <figure key={key} className="my-6">
          <img
            src={block.url}
            alt={block.alt_text || block.caption || ''}
            loading="lazy"
            className="w-full rounded-xl object-cover"
          />
          {(block.caption || block.credit) && (
            <figcaption className="mt-2 text-xs text-[#5B616E]">
              {block.caption}
              {block.credit ? <span className="ml-2 italic">— {block.credit}</span> : null}
            </figcaption>
          )}
        </figure>
      );
    case 'video':
      return (
        <figure key={key} className="my-6">
          {block.url && (block.url.endsWith('.mp4') || block.url.endsWith('.m3u8')) ? (
            <video src={block.url} controls className="w-full rounded-xl" />
          ) : (
            <div className="aspect-video rounded-xl overflow-hidden">
              <iframe
                src={block.url}
                title={block.caption || 'Video'}
                className="w-full h-full"
                allowFullScreen
                sandbox="allow-scripts allow-same-origin allow-presentation"
              />
            </div>
          )}
          {block.caption && <figcaption className="mt-2 text-xs text-[#5B616E]">{block.caption}</figcaption>}
        </figure>
      );
    case 'audio':
      return (
        <figure key={key} className="my-6">
          {block.url && <audio controls src={block.url} className="w-full" />}
          {block.caption && <figcaption className="mt-2 text-xs text-[#5B616E]">{block.caption}</figcaption>}
        </figure>
      );
    case 'gallery': {
      const images = (block.images || []).filter((i) => i.url);
      if (!images.length) return null;
      return (
        <div key={key} className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {images.map((img, i) => (
            <figure key={i} className="rounded-xl overflow-hidden">
              <img src={img.url} alt={img.alt_text || img.caption || ''} loading="lazy" className="w-full h-56 object-cover" />
              {img.caption && <figcaption className="text-xs text-[#5B616E] px-1 py-2">{img.caption}</figcaption>}
            </figure>
          ))}
        </div>
      );
    }
    case 'quote':
      return (
        <blockquote key={key} className="my-6 border-l-4 border-[#B89B5E] pl-4 italic text-lg leading-relaxed text-[#1A1A1A]">
          “{block.content}”
          {block.attribution && (
            <cite className="block mt-2 not-italic text-xs text-[#5B616E]">— {block.attribution}</cite>
          )}
        </blockquote>
      );
    case 'callout': {
      const variant = block.variant || 'info';
      const style = CALLOUT_STYLES[variant] || CALLOUT_STYLES.info;
      return (
        <aside
          key={key}
          className="my-6 p-4 rounded-xl"
          style={{ background: style.bg, borderLeft: `4px solid ${style.border}` }}
        >
          <p className="text-sm leading-6">
            <span className="mr-2">{style.icon}</span>
            {block.content}
          </p>
        </aside>
      );
    }
    case 'embed':
      if (isSafeIframeMarkup(block.content)) {
        return (
          <div
            key={key}
            className="my-6 rounded-xl overflow-hidden"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: block.content }}
          />
        );
      }
      return (
        <pre key={key} className="my-6 text-xs bg-[#FBF7EF] border border-[#E7E0D6] rounded-xl p-3 overflow-x-auto">
          {block.content}
        </pre>
      );
    case 'divider':
      return <hr key={key} className="my-8 border-t-2 border-[#E7E0D6]" />;
    case 'data_viz': {
      const rows = csvToRows(block.csv_data);
      return (
        <div key={key} className="my-6">
          {block.title && <p className="font-semibold mb-3">{block.title}</p>}
          {block.viz_type === 'table' ? <DataTable rows={rows} /> : <MiniBarChart rows={rows} />}
        </div>
      );
    }
    case 'related': {
      const links = (block.content || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      if (!links.length) return null;
      return (
        <aside key={key} className="my-6 p-4 rounded-xl bg-[#FBF7EF] border border-[#E7E0D6]">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#5B616E] mb-2">Related</p>
          <ul className="space-y-1">
            {links.map((href, i) => (
              <li key={i}>
                <a href={href} className="text-sm text-[#1E5A8A] underline">{href}</a>
              </li>
            ))}
          </ul>
        </aside>
      );
    }
    default:
      return (
        <div key={key} className="prose-news leading-7">
          <Markdown>{block.content || ''}</Markdown>
        </div>
      );
  }
}

export default function BlockRenderer({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <div data-testid="article-blocks" className="article-blocks space-y-3">
      {blocks.map((b, i) => renderBlock(b, b.id || i))}
    </div>
  );
}
